import clsx from 'clsx';
import React from 'react';
import Dialog, { DialogSize } from '../Dialog/Dialog';
import useDialogWizard from './useDialogWizard';

export type DialogWizardProps = Readonly<{
  ariaLabel?: string;
  buttonTextBack?: string;
  buttonTextFinish?: string;
  buttonTextNext?: string;
  children: React.ReactNode;
  className?: string;
  currentStep?: number;
  iconTitle?: string;
  isNextStepDisabled?: boolean;
  isOpen?: boolean;
  onCancel?: () => void;
  onComplete?: () => void;
  onStepChange?: (nextStep: number) => void;
  showCurrentStep?: boolean;
  size?: DialogSize;
  text?: string;
  title?: React.ReactNode;
  validateStep?: (currentStep: number) => boolean;
}>;

function DialogWizard({
  ariaLabel,
  buttonTextBack,
  buttonTextFinish,
  buttonTextNext,
  children,
  className,
  currentStep,
  iconTitle,
  isNextStepDisabled = false,
  isOpen = false,
  onCancel,
  onComplete,
  onStepChange,
  showCurrentStep = true,
  size = 'l',
  text,
  title,
  validateStep,
}: DialogWizardProps): React.JSX.Element {
  const {
    customFooterLeft,
    handleCancel,
    handleNext,
    nextButtonText,
    resolvedCurrentStep,
    stepIndicatorText,
    stepRef,
    steps,
  } = useDialogWizard({
    buttonTextBack,
    buttonTextFinish,
    buttonTextNext,
    children,
    currentStep,
    onCancel,
    onComplete,
    onStepChange,
    validateStep,
  });

  return (
    <Dialog
      ariaLabel={ariaLabel}
      buttonTextSuccess={nextButtonText}
      className={clsx('dialog-wizard', className)}
      customFooterLeft={customFooterLeft}
      iconTitle={iconTitle}
      isDisabledSuccess={isNextStepDisabled}
      isOpen={isOpen}
      onCancel={handleCancel}
      onClose={handleCancel}
      onSuccess={handleNext}
      size={size}
      text={text}
      title={title}
    >
      <div className="dialog-wizard__content">
        {showCurrentStep && (
          <div className="dialog-wizard__step-indicator" aria-live="polite">
            <span className="dialog-wizard__step-text">{stepIndicatorText}</span>
          </div>
        )}
        <div className="dialog-wizard__step-content" ref={stepRef} tabIndex={-1}>
          {steps[resolvedCurrentStep] ?? null}
        </div>
      </div>
    </Dialog>
  );
}

export default DialogWizard;
